import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Heart, Star, Bell, Search, X, Check, Loader2, Crown, Lock } from "lucide-react";
import { useUserFavorites, AVAILABLE_LEAGUES, type FavoriteTeam } from "@/hooks/useUserFavorites";
import { useUserFavoritesSync } from "@/hooks/useUserFavoritesSync";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { NHL_TEAMS } from "@/data/nhlTeams";
import { SHL_TEAMS } from "@/data/shlTeams";
import { KHL_TEAMS } from "@/data/khlTeams";
import { LIIGA_TEAMS } from "@/data/liigaTeams";
import { AHL_TEAMS } from "@/data/ahlTeams";
import { ECHL_TEAMS } from "@/data/echlTeams";
import { EXTRALIGA_TEAMS } from "@/data/extraligaTeams";
import { DEL_TEAMS } from "@/data/delTeams";
import { NL_TEAMS } from "@/data/nlTeams";
import { ALLSVENSKAN_TEAMS } from "@/data/allsvenskanTeams";

interface FavoriteTeamsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Get teams for a specific league
const getTeamsForLeague = (leagueId: string): FavoriteTeam[] => {
  switch (leagueId) {
    case "NHL":
      return NHL_TEAMS.map((t) => ({
        id: t.id,
        name: `${t.city} ${t.name}`,
        abbr: t.abbr,
        league: "NHL",
        logo: t.logo,
      }));
    case "SHL":
      return SHL_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "SHL",
        logo: t.logo,
      }));
    case "KHL":
      return KHL_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "KHL",
        logo: t.logo,
      }));
    case "Liiga":
      return LIIGA_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "Liiga",
        logo: t.logo,
      }));
    case "AHL":
      return AHL_TEAMS.map((t) => ({
        id: t.id,
        name: `${t.city} ${t.name}`,
        abbr: t.abbr,
        league: "AHL",
        logo: t.logo,
      }));
    case "ECHL":
      return ECHL_TEAMS.map((t) => ({
        id: t.id,
        name: `${t.city} ${t.name}`,
        abbr: t.abbr,
        league: "ECHL",
        logo: t.logo,
      }));
    case "Extraliga":
      return EXTRALIGA_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "Extraliga",
        logo: t.logo,
      }));
    case "DEL":
      return DEL_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "DEL",
        logo: t.logo,
      }));
    case "NL":
      return NL_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "NL",
        logo: t.logo,
      }));
    case "Allsvenskan":
      return ALLSVENSKAN_TEAMS.map((t) => ({
        id: t.id,
        name: t.name,
        abbr: t.abbr,
        league: "Allsvenskan",
        logo: t.logo,
      }));
    default:
      return [];
  }
};

export function FavoriteTeamsModal({ isOpen, onClose }: FavoriteTeamsModalProps) {
  const {
    favorites,
    addLeague,
    removeLeague,
    addTeam,
    removeTeam,
    toggleAlerts,
    isFavoriteTeam,
    isFavoriteLeague,
  } = useUserFavorites();

  const { user, subscription } = useAuth();
  const { saveTeams, isSaving, dbTeams } = useUserFavoritesSync();
  
  const canEdit = subscription.subscribed && (subscription.tier === "pro" || subscription.tier === "elite");
  const isMobile = useIsMobile();

  const [selectedLeagueTab, setSelectedLeagueTab] = useState<string>("NHL");
  const [teamSearch, setTeamSearch] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes when teams are modified
  useEffect(() => {
    if (isOpen) {
      setHasChanges(false);
    }
  }, [isOpen]);

  const teamsForSelectedLeague = useMemo(() => {
    const teams = getTeamsForLeague(selectedLeagueTab);
    if (!teamSearch) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
        t.abbr.toLowerCase().includes(teamSearch.toLowerCase())
    );
  }, [selectedLeagueTab, teamSearch]);

  const handleLeagueToggle = (leagueId: string) => {
    if (!canEdit) return;
    if (isFavoriteLeague(leagueId)) {
      removeLeague(leagueId);
    } else {
      addLeague(leagueId);
    }
    setHasChanges(true);
  };

  const handleTeamToggle = (team: FavoriteTeam) => {
    if (!canEdit) return;
    if (isFavoriteTeam(team.id, team.league)) {
      removeTeam(team.id, team.league);
    } else {
      addTeam(team);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user || !canEdit) return;
    
    await saveTeams(favorites.teams);
    setHasChanges(false);
    onClose();
  };

  const TeamsContent = () => (
    <>
      <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2">
        {/* Favorite Leagues Section */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-primary" />
            Select Leagues
          </h3>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_LEAGUES.map((league) => (
              <button
                key={league.id}
                onClick={() => handleLeagueToggle(league.id)}
                disabled={!canEdit}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isFavoriteLeague(league.id)
                    ? "bg-primary text-primary-foreground ring-2 ring-primary/50"
                    : canEdit
                      ? "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80"
                      : "bg-secondary/50 text-muted-foreground cursor-not-allowed"
                }`}
              >
                <span>{league.flag}</span>
                <span>{league.name}</span>
                {isFavoriteLeague(league.id) && <Check className="w-3 h-3" />}
              </button>
            ))}
          </div>
        </div>

        {/* Teams Selection Section */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2 flex-shrink-0">
            <Heart className="w-4 h-4 text-primary" />
            Select Teams
          </h3>

          {/* League Tabs */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-2 scrollbar-thin">
            {AVAILABLE_LEAGUES.map((league) => (
              <button
                key={league.id}
                onClick={() => {
                  setSelectedLeagueTab(league.id);
                  setTeamSearch("");
                }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedLeagueTab === league.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                }`}
              >
                {league.flag} {league.id}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-3 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className="pl-9 bg-secondary border-border"
            />
            {teamSearch && (
              <button
                onClick={() => setTeamSearch("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Teams List */}
          <ScrollArea className="h-[25vh] pr-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {teamsForSelectedLeague.map((team) => {
                const isSelected = isFavoriteTeam(team.id, team.league);
                return (
                  <button
                    key={`${team.league}-${team.id}`}
                    onClick={() => handleTeamToggle(team)}
                    disabled={!canEdit}
                    className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                      isSelected
                        ? "bg-primary/20 border border-primary/50 ring-1 ring-primary/30"
                        : canEdit
                          ? "bg-secondary/50 border border-transparent hover:bg-secondary hover:border-border"
                          : "bg-secondary/30 border border-transparent cursor-not-allowed opacity-60"
                    }`}
                  >
                    {team.logo ? (
                      <img
                        src={team.logo}
                        alt={team.name}
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground">
                        {team.abbr}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{team.name}</p>
                      <p className="text-xs text-muted-foreground">{team.abbr}</p>
                    </div>
                    {isSelected && (
                      <Heart className="w-4 h-4 text-primary fill-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Selected Teams Summary */}
        {favorites.teams.length > 0 && (
          <div className="border-t border-border pt-4">
            <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              Your Teams ({favorites.teams.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {favorites.teams.map((team) => (
                <Badge
                  key={`${team.league}-${team.id}`}
                  variant="secondary"
                  className="flex items-center gap-1.5 pr-1"
                >
                  <span>{team.name}</span>
                  <button
                    onClick={() => removeTeam(team.id, team.league)}
                    className="hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Toggle */}
        <div className="flex items-center justify-between py-3 px-4 bg-secondary/50 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-primary" />
            <div>
              <Label htmlFor="alerts-toggle" className="text-sm font-medium text-foreground">
                Enable game alerts
              </Label>
              <p className="text-xs text-muted-foreground">
                Get notified when your teams play
              </p>
            </div>
          </div>
          <Switch
            id="alerts-toggle"
            checked={favorites.alertsEnabled}
            onCheckedChange={toggleAlerts}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border flex-shrink-0">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save to Account
              </>
            )}
          </Button>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="flex items-center gap-2 text-foreground">
              <Heart className="w-5 h-5 text-primary" />
              Follow Your Favorite Teams
            </DrawerTitle>
            {!canEdit && user && (
              <div className="flex items-center gap-2 mt-2 text-xs text-amber-400">
                <Lock className="w-3 h-3" />
                <span>Upgrade to Pro or Elite to customize your teams</span>
                <Crown className="w-3 h-3" />
              </div>
            )}
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6 flex flex-col gap-4">
            <TeamsContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col bg-background border-border">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Heart className="w-5 h-5 text-primary" />
            Follow Your Favorite Teams
          </DialogTitle>
          {!canEdit && user && (
            <div className="flex items-center gap-2 mt-2 text-xs text-amber-400">
              <Lock className="w-3 h-3" />
              <span>Upgrade to Pro or Elite to customize your teams</span>
              <Crown className="w-3 h-3" />
            </div>
          )}
        </DialogHeader>
        <TeamsContent />
      </DialogContent>
    </Dialog>
  );
}

export default FavoriteTeamsModal;
